/**
 * depthMath.js — ГЕОМЕТРИЯ ГЛУБИНЫ. Чистые функции, ни одного вызова GL.
 * Mikhmel Cornerstone4D DICOM Viewer Pro — 10.19.18
 *
 * ПОВОД. В 10.19.17 выяснилось, что «глубина» в программе была подделкой:
 * яркость цветного кадра выдавалась за карту глубины. Подделку я убрал, а
 * вместе с ней пропали затенение, отражения, туман и глубина резкости — им
 * нечем работать. Обещал сделать настоящую глубину отдельным заходом; вот он.
 *
 * ПОЧЕМУ НЕЛЬЗЯ ВЗЯТЬ ГОТОВУЮ. Проверено по исходникам vtk.js 32.x, который
 * рисует объём внутри Cornerstone3D:
 *
 *   • в шейдере объёмного маппера (`glsl/vtkVolumeFS.glsl.js`) НЕТ ни одного
 *     `gl_FragDepth` — объём не пишет глубину вообще. То есть даже добравшись до
 *     аппаратного z-буфера, мы нашли бы там дальнюю плоскость и ничего больше;
 *   • контекст vtk.js создаётся без `antialias: false` (RenderWindow.js,
 *     `get3DContext`), то есть буфер многосэмпловый, а `blitFramebuffer` с
 *     DEPTH_BUFFER_BIT из многосэмплового в обычный запрещён;
 *   • `readPixels` с depth-вложения запрещён в WebGL2 в любом случае.
 *
 * Значит, глубину надо СЧИТАТЬ САМИМ — по тому же объёму и по той же камере,
 * которыми рисуется картинка. Здесь лежит вся геометрия этого расчёта, отдельно
 * от WebGL: луч через пиксель, вход/выход из коробки объёма, переход
 * мир↔индексы, нормировка глубины. Отдельно — чтобы это можно было проверить
 * числами, без браузера и без видеокарты (tests/depth_test.mjs), и чтобы шейдер
 * считал ровно то же самое.
 */



/* ── Мелкая векторная арифметика (без зависимостей) ───────────────────────── */

export const вычесть = (a, b) => [a[0] - b[0], a[1] - b[1], a[2] - b[2]];
export const скалярно = (a, b) => a[0] * b[0] + a[1] * b[1] + a[2] * b[2];
export const длина = (a) => Math.sqrt(скалярно(a, a));
export function нормировать(a) {
  const l = длина(a);
  return l > 1e-12 ? [a[0] / l, a[1] / l, a[2] / l] : [0, 0, 0];
}

/* ── Матрицы 4×4, столбцовый порядок (как у WebGL) ─────────────────────────
 *
 * Свои, а не из cinematic10/nerfRenderer.js — и это не дублирование ради
 * дублирования. Тот модуль тянет за собой webgpuBackend.js, а тот при загрузке
 * обращается к GPUTextureUsage — константе, которой в Node нет вовсе. Импорт
 * оттуда означал бы, что геометрию глубины НЕВОЗМОЖНО проверить без браузера, а
 * именно её и надо проверять числами. Совпадение с общим mat4 закреплено
 * проверкой в tests/depth_test.mjs.
 */
export const мат4 = {
  умножить(a, b) {
    const r = new Float32Array(16);
    for (let i = 0; i < 4; i++) {
      for (let j = 0; j < 4; j++) {
        r[i * 4 + j] = a[j] * b[i * 4] + a[4 + j] * b[i * 4 + 1]
          + a[8 + j] * b[i * 4 + 2] + a[12 + j] * b[i * 4 + 3];
      }
    }
    return r;
  },
  обратить(m) {
    const a00 = m[0], a01 = m[1], a02 = m[2], a03 = m[3];
    const a10 = m[4], a11 = m[5], a12 = m[6], a13 = m[7];
    const a20 = m[8], a21 = m[9], a22 = m[10], a23 = m[11];
    const a30 = m[12], a31 = m[13], a32 = m[14], a33 = m[15];
    const b00 = a00 * a11 - a01 * a10, b01 = a00 * a12 - a02 * a10;
    const b02 = a00 * a13 - a03 * a10, b03 = a01 * a12 - a02 * a11;
    const b04 = a01 * a13 - a03 * a11, b05 = a02 * a13 - a03 * a12;
    const b06 = a20 * a31 - a21 * a30, b07 = a20 * a32 - a22 * a30;
    const b08 = a20 * a33 - a23 * a30, b09 = a21 * a32 - a22 * a31;
    const b10 = a21 * a33 - a23 * a31, b11 = a22 * a33 - a23 * a32;
    let det = b00 * b11 - b01 * b10 + b02 * b09 + b03 * b08 - b04 * b07 + b05 * b06;
    if (!det) return new Float32Array([1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1]);
    det = 1 / det;
    return new Float32Array([
      (a11 * b11 - a12 * b10 + a13 * b09) * det, (a02 * b10 - a01 * b11 - a03 * b09) * det,
      (a31 * b05 - a32 * b04 + a33 * b03) * det, (a22 * b04 - a21 * b05 - a23 * b03) * det,
      (a12 * b08 - a10 * b11 - a13 * b07) * det, (a00 * b11 - a02 * b08 + a03 * b07) * det,
      (a32 * b02 - a30 * b05 - a33 * b01) * det, (a20 * b05 - a22 * b02 + a23 * b01) * det,
      (a10 * b10 - a11 * b08 + a13 * b06) * det, (a01 * b08 - a00 * b10 - a03 * b06) * det,
      (a30 * b04 - a31 * b02 + a33 * b00) * det, (a21 * b02 - a20 * b04 - a23 * b00) * det,
      (a11 * b07 - a10 * b09 - a12 * b06) * det, (a00 * b09 - a01 * b07 + a02 * b06) * det,
      (a31 * b01 - a30 * b03 - a32 * b00) * det, (a20 * b03 - a21 * b01 + a22 * b00) * det,
    ]);
  },
  перспектива(fovYRad, aspect, near, far) {
    const f = 1 / Math.tan(fovYRad / 2);
    const nf = 1 / (near - far);
    return new Float32Array([
      f / aspect, 0, 0, 0,
      0, f, 0, 0,
      0, 0, (far + near) * nf, -1,
      0, 0, 2 * far * near * nf, 0,
    ]);
  },
  взгляд(eye, center, up) {
    const z = нормировать(вычесть(eye, center));
    let x = [up[1] * z[2] - up[2] * z[1], up[2] * z[0] - up[0] * z[2], up[0] * z[1] - up[1] * z[0]];
    if (длина(x) < 1e-9) {           // взгляд вдоль «верха» — подменяем ось
      const альт = Math.abs(z[2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
      x = [альт[1] * z[2] - альт[2] * z[1], альт[2] * z[0] - альт[0] * z[2], альт[0] * z[1] - альт[1] * z[0]];
    }
    x = нормировать(x);
    const y = [z[1] * x[2] - z[2] * x[1], z[2] * x[0] - z[0] * x[2], z[0] * x[1] - z[1] * x[0]];
    return new Float32Array([
      x[0], y[0], z[0], 0,
      x[1], y[1], z[1], 0,
      x[2], y[2], z[2], 0,
      -(x[0] * eye[0] + x[1] * eye[1] + x[2] * eye[2]),
      -(y[0] * eye[0] + y[1] * eye[1] + y[2] * eye[2]),
      -(z[0] * eye[0] + z[1] * eye[1] + z[2] * eye[2]),
      1,
    ]);
  },
};

/**
 * Камера объёмного окна Cornerstone3D → то, из чего строятся матрицы.
 *
 * Cornerstone3D отдаёт `viewAngle` (вертикальный угол зрения в градусах),
 * `clippingRange`, признак параллельной проекции и `parallelScale`. Разбирать
 * их приходится здесь, потому что для параллельной и перспективной проекции
 * матрицы разные, а «универсального» пути в vtk.js наружу не выведено.
 *
 * @param {object} cam результат viewport.getCamera()
 * @param {number} aspect ширина/высота окна
 */
export function разобратьКамеру(cam, aspect) {
  if (!cam || !cam.position || !cam.focalPoint) return { ок: false, причина: 'камера окна не отдала положение' };
  const eye = [cam.position[0], cam.position[1], cam.position[2]];
  const target = [cam.focalPoint[0], cam.focalPoint[1], cam.focalPoint[2]];
  const up = cam.viewUp ? [cam.viewUp[0], cam.viewUp[1], cam.viewUp[2]] : [0, 1, 0];
  const forward = нормировать(вычесть(target, eye));
  const расстояние = длина(вычесть(target, eye));

  // Диапазон отсечения: если окно его не дало — берём с запасом вокруг цели.
  // Без этого глубина нормировалась бы непонятно к чему.
  let near = 0.1, far = расстояние * 2 + 1;
  if (Array.isArray(cam.clippingRange) && cam.clippingRange.length === 2
      && cam.clippingRange[1] > cam.clippingRange[0] && cam.clippingRange[0] >= 0) {
    near = Math.max(1e-3, cam.clippingRange[0]);
    far = cam.clippingRange[1];
  }

  const параллельная = !!cam.parallelProjection;
  const parallelScale = cam.parallelScale > 0 ? cam.parallelScale : Math.max(1, расстояние * 0.4);
  const viewAngle = cam.viewAngle > 0 ? cam.viewAngle : 30;

  return {
    ок: true, eye, target, up, forward, расстояние,
    near, far, параллельная, parallelScale, viewAngle,
    aspect: aspect > 0 ? aspect : 1,
  };
}

/** Ортографическая проекция (столбцовый порядок, как у mat4 в проекте). */
export function ортоМатрица(l, r, b, t, n, f) {
  return new Float32Array([
    2 / (r - l), 0, 0, 0,
    0, 2 / (t - b), 0, 0,
    0, 0, -2 / (f - n), 0,
    -(r + l) / (r - l), -(t + b) / (t - b), -(f + n) / (f - n), 1,
  ]);
}

/**
 * Матрицы вида и проекции по разобранной камере.
 * @returns {{view: Float32Array, proj: Float32Array, viewProj: Float32Array, invViewProj: Float32Array}}
 */
export function матрицыКамеры(к) {
  const view = мат4.взгляд(к.eye, к.target, к.up);
  const proj = к.параллельная
    ? ортоМатрица(-к.parallelScale * к.aspect, к.parallelScale * к.aspect,
      -к.parallelScale, к.parallelScale, к.near, к.far)
    : мат4.перспектива(к.viewAngle * Math.PI / 180, к.aspect, к.near, к.far);
  const viewProj = мат4.умножить(proj, view);
  return { view, proj, viewProj, invViewProj: мат4.обратить(viewProj) };
}

/** Умножение вектора-точки на матрицу 4×4 (столбцовый порядок) с делением на w. */
export function точкаЧерезМатрицу(m, p) {
  const x = p[0], y = p[1], z = p[2];
  const w = m[3] * x + m[7] * y + m[11] * z + m[15];
  const iw = Math.abs(w) > 1e-12 ? 1 / w : 0;
  return [
    (m[0] * x + m[4] * y + m[8] * z + m[12]) * iw,
    (m[1] * x + m[5] * y + m[9] * z + m[13]) * iw,
    (m[2] * x + m[6] * y + m[10] * z + m[14]) * iw,
  ];
}

/**
 * Луч через пиксель. Точка NDC берётся на БЛИЖНЕЙ и ДАЛЬНЕЙ плоскостях и
 * переводится в мир обратной матрицей — так один и тот же код работает и для
 * перспективы, и для параллельной проекции. Ни одного «если параллельная».
 *
 * @param {Float32Array} invViewProj обратная матрица вида-проекции
 * @param {number} ndcX -1…1
 * @param {number} ndcY -1…1
 */
export function лучЧерезПиксель(invViewProj, ndcX, ndcY) {
  const близко = точкаЧерезМатрицу(invViewProj, [ndcX, ndcY, -1]);
  const далеко = точкаЧерезМатрицу(invViewProj, [ndcX, ndcY, 1]);
  return { начало: близко, направление: нормировать(вычесть(далеко, близко)) };
}

/**
 * Пересечение луча с осевой коробкой (алгоритм плит).
 * @returns {{вход: number, выход: number}|null} null — луч мимо коробки
 */
export function пересечьКоробку(начало, направление, минимум, максимум) {
  let t0 = -Infinity, t1 = Infinity;
  for (let i = 0; i < 3; i++) {
    const d = направление[i];
    if (Math.abs(d) < 1e-12) {
      if (начало[i] < минимум[i] || начало[i] > максимум[i]) return null;
      continue;
    }
    const инв = 1 / d;
    let a = (минимум[i] - начало[i]) * инв;
    let b = (максимум[i] - начало[i]) * инв;
    if (a > b) { const п = a; a = b; b = п; }
    if (a > t0) t0 = a;
    if (b < t1) t1 = b;
    if (t0 > t1) return null;
  }
  if (t1 < 0) return null;
  return { вход: Math.max(t0, 0), выход: t1 };
}

/**
 * Матрица «мир → индексы объёма» как три строки 3×3 плюс сдвиг.
 *
 * Индекс = M⁻¹·(мир − origin), где M = направление(3×3) · diag(spacing).
 * Для обычной аксиальной серии направление единичное, и всё сводится к делению
 * на шаг; но косые серии существуют, и молча считать их прямыми нельзя.
 *
 * @param {number[]} origin начало объёма (мир)
 * @param {number[]} spacing шаг по осям
 * @param {number[]|null} direction 9 чисел, по столбцам (как у vtk), или null
 * @returns {{матрица: number[], origin: number[]}|null} матрица 3×3 по строкам
 */
export function мирВИндексы(origin, spacing, direction) {
  const d = (direction && direction.length === 9) ? direction : [1, 0, 0, 0, 1, 0, 0, 0, 1];
  // M по столбцам: столбец i = direction_i * spacing[i]
  const m = [
    d[0] * spacing[0], d[3] * spacing[1], d[6] * spacing[2],
    d[1] * spacing[0], d[4] * spacing[1], d[7] * spacing[2],
    d[2] * spacing[0], d[5] * spacing[1], d[8] * spacing[2],
  ];
  const det = m[0] * (m[4] * m[8] - m[5] * m[7])
            - m[1] * (m[3] * m[8] - m[5] * m[6])
            + m[2] * (m[3] * m[7] - m[4] * m[6]);
  if (!isFinite(det) || Math.abs(det) < 1e-20) return null;
  const и = 1 / det;
  const обр = [
    (m[4] * m[8] - m[5] * m[7]) * и, (m[2] * m[7] - m[1] * m[8]) * и, (m[1] * m[5] - m[2] * m[4]) * и,
    (m[5] * m[6] - m[3] * m[8]) * и, (m[0] * m[8] - m[2] * m[6]) * и, (m[2] * m[3] - m[0] * m[5]) * и,
    (m[3] * m[7] - m[4] * m[6]) * и, (m[1] * m[6] - m[0] * m[7]) * и, (m[0] * m[4] - m[1] * m[3]) * и,
  ];
  return { матрица: обр, origin: [origin[0], origin[1], origin[2]] };
}

/** Применить «мир → индексы» к точке. */
export function вИндексы(преобр, точка) {
  const p = вычесть(точка, преобр.origin);
  const m = преобр.матрица;
  return [
    m[0] * p[0] + m[1] * p[1] + m[2] * p[2],
    m[3] * p[0] + m[4] * p[1] + m[5] * p[2],
    m[6] * p[0] + m[7] * p[1] + m[8] * p[2],
  ];
}

/** Применить «мир → индексы» к НАПРАВЛЕНИЮ (без сдвига). */
export function направлениеВИндексы(преобр, вектор) {
  const m = преобр.матрица;
  return [
    m[0] * вектор[0] + m[1] * вектор[1] + m[2] * вектор[2],
    m[3] * вектор[0] + m[4] * вектор[1] + m[5] * вектор[2],
    m[6] * вектор[0] + m[7] * вектор[1] + m[8] * вектор[2],
  ];
}

/**
 * Перевод ГРАДИЕНТА из индексов объёма в мир — ТРАНСПОНИРОВАННОЙ матрицей
 * «мир → индексы». [10.19.21]
 *
 * Почему транспонированной, а не обратной. Непрозрачность в шейдере — функция
 * ИНДЕКСОВ: a(idx), а idx = A·(мир − origin), где A — матрица «мир → индексы».
 * По правилу цепочки ∇_мир a = Aᵀ·∇_инд a. Для чистого поворота Aᵀ и A⁻¹
 * совпадают, и ошибку не видно; расходятся они на АНИЗОТРОПНОМ шаге — а шаг КТ
 * анизотропен почти всегда (0,7×0,7×2,5 мм — обычное дело). Поэтому проверка в
 * tests/normals_test.mjs гоняет именно анизотропный случай: на нём «обратная
 * вместо транспонированной» даёт нормаль, повёрнутую на десятки градусов.
 *
 * @param {{матрица: number[]}} преобр результат мирВИндексы()
 * @param {number[]} г градиент в индексах
 * @returns {number[]} градиент в мире (НЕ нормированный)
 */
export function градиентВМир(преобр, г) {
  const m = преобр.матрица; // 3×3 по строкам; транспонирование — чтение по столбцам
  return [
    m[0] * г[0] + m[3] * г[1] + m[6] * г[2],
    m[1] * г[0] + m[4] * г[1] + m[7] * г[2],
    m[2] * г[0] + m[5] * г[1] + m[8] * г[2],
  ];
}

/**
 * Нормаль поверхности из градиента непрозрачности. [10.19.21]
 *
 * Непрозрачность растёт ВНУТРЬ вещества, значит градиент смотрит внутрь, а
 * наружная нормаль — минус нормированный градиент. На сфере это обязано дать
 * (точка − центр)/радиус, и ровно это закреплено проверкой.
 *
 * @returns {number[]|null} null — градиент вырожден, нормали нет (честный отказ)
 */
export function нормальИзГрадиента(преобр, градиентИндексы) {
  const g = градиентВМир(преобр, градиентИндексы);
  const l = длина(g);
  if (!(l > 1e-12)) return null;
  return [-g[0] / l, -g[1] / l, -g[2] / l];
}

/**
 * Трилинейная выборка объёма в НЕПРЕРЫВНЫХ индексах — как texture() у GL.
 * [10.19.21]
 *
 * Соответствие точное: шейдер берёт uvw = (idx + 0.5)/dims при LINEAR и
 * CLAMP_TO_EDGE, то есть целый индекс i попадает в центр текселя i, а за краем
 * значение равно краевому. Здесь то же самое, только числами — чтобы нормаль
 * можно было проверить без браузера и без видеокарты.
 *
 * @param {ArrayLike<number>} данные объём, раскладка (z·dy + y)·dx + x
 * @param {number[]} dims [dx, dy, dz]
 * @param {number[]} p непрерывные индексы
 */
export function трилинейно(данные, dims, p) {
  const [dx, dy, dz] = dims;
  const кл = (v, верх) => (v < 0 ? 0 : (v > верх ? верх : v));
  const x = кл(p[0], dx - 1), y = кл(p[1], dy - 1), z = кл(p[2], dz - 1);
  const x0 = Math.floor(x), y0 = Math.floor(y), z0 = Math.floor(z);
  const x1 = Math.min(x0 + 1, dx - 1), y1 = Math.min(y0 + 1, dy - 1), z1 = Math.min(z0 + 1, dz - 1);
  const fx = x - x0, fy = y - y0, fz = z - z0;
  const в = (xx, yy, zz) => данные[(zz * dy + yy) * dx + xx];
  const c00 = в(x0, y0, z0) * (1 - fx) + в(x1, y0, z0) * fx;
  const c10 = в(x0, y1, z0) * (1 - fx) + в(x1, y1, z0) * fx;
  const c01 = в(x0, y0, z1) * (1 - fx) + в(x1, y0, z1) * fx;
  const c11 = в(x0, y1, z1) * (1 - fx) + в(x1, y1, z1) * fx;
  return (c00 * (1 - fy) + c10 * fy) * (1 - fz) + (c01 * (1 - fy) + c11 * fy) * fz;
}

/**
 * Градиент объёма центральными разностями — ШЕСТЬ выборок, как в шейдере.
 * [10.19.21] Шаг — в индексах (по умолчанию один воксель); анизотропию шага
 * выправляет последующий перевод в мир (градиентВМир), здесь её учитывать
 * нельзя — иначе она учлась бы дважды.
 */
export function градиентОбъёма(данные, dims, p, шаг = 1) {
  return [
    трилинейно(данные, dims, [p[0] + шаг, p[1], p[2]]) - трилинейно(данные, dims, [p[0] - шаг, p[1], p[2]]),
    трилинейно(данные, dims, [p[0], p[1] + шаг, p[2]]) - трилинейно(данные, dims, [p[0], p[1] - шаг, p[2]]),
    трилинейно(данные, dims, [p[0], p[1], p[2] + шаг]) - трилинейно(данные, dims, [p[0], p[1], p[2] - шаг]),
  ];
}

/**
 * Линейная нормировка глубины: расстояние ВДОЛЬ ОСИ ВЗГЛЯДА, а не по лучу.
 *
 * Почему вдоль оси взгляда: именно так устроен буфер глубины, и именно такие
 * величины сравнивают экранные эффекты. Почему линейная, а не как у железа
 * (1/z): разности линейной глубины пропорциональны настоящим миллиметрам, и у
 * порогов SSAO появляется смысл. У аппаратной глубины разность между соседними
 * пикселями зависит от того, где они находятся, и порог «0,01» не значит
 * ничего.
 *
 * @returns {number} 0 — у ближней плоскости, 1 — у дальней и «не попал»
 */
export function линейнаяГлубина(вдольОси, near, far) {
  if (!(far > near)) return 1;
  return Math.min(1, Math.max(0, (вдольОси - near) / (far - near)));
}

/**
 * Первое попадание луча в сферу — только для ПРОВЕРОК: сфера единственная
 * фигура, для которой глубину можно посчитать формулой и сверить с тем, что
 * выдал расчёт по объёму.
 * @returns {number|null} расстояние по лучу до ближнего пересечения
 */
export function попаданиеВСферу(начало, направление, центр, радиус) {
  const oc = вычесть(начало, центр);
  const b = скалярно(oc, направление);
  const c = скалярно(oc, oc) - радиус * радиус;
  const d = b * b - c;
  if (d < 0) return null;
  const к = Math.sqrt(d);
  const t1 = -b - к, t2 = -b + к;
  if (t1 >= 0) return t1;
  if (t2 >= 0) return t2;
  return null;
}

export default {
  разобратьКамеру, матрицыКамеры, лучЧерезПиксель, пересечьКоробку,
  мирВИндексы, вИндексы, направлениеВИндексы, линейнаяГлубина, попаданиеВСферу,
  градиентВМир, нормальИзГрадиента, трилинейно, градиентОбъёма,
  точкаЧерезМатрицу, ортоМатрица, нормировать, вычесть, скалярно, длина, мат4,
};
